import { FormGroup } from "../../../components/form/models/models";
import * as Yup from "yup";
import { FormArrayConfig } from "../../enums/FormArrayConfig";

export const supportingDocumentsForm: FormGroup = {
  type: "formGroup",
  fields: [
    {
      type: "field",
      key: "supportingDocumentsConsentForm",
      fieldType: "file",
      props: {
        maxNumberOfFiles: 1,
      },
      validation: Yup.object({
        files: Yup.array().min(1, "Application cannot be submitted unless a document has been uploaded").max(1, "Only one (1) file may be uploaded"),
      }),
      label: "Please upload the parent/carer/guardian consent form",
      warn: (
        <span className="text-primary">
          You must use this{" "}
          <a
            className="underline "
            href="https://www.ahaconsulting.com.au/wp-content/uploads/2025/08/KIS-privacy-notice-and-consent-form.pdf"
            target="_blank"
            title="Link opens in a new tab"
            rel="noreferrer">
            template
          </a>{" "}
          and ensure that all fields are completed
        </span>
      ),
      //dynamic set values
      expressionProps: {
        uuid: (context) => context.uuid,
        isManagerRole: (context) => context.isManagerRole,
        isPresub: (context) => context.isPresub,
        isCurrent: (context) => context.isCurrent,
        applicationBaseId: (context) => context.applicationBaseId,
        isAssessorRole: (context) => context.isAssessorRole,
        isKindergartenRole: (context) => context.isKindergartenRole,
      },
    },
    {
      type: "field",
      key: "complexMedicalNeedsEvidence",
      fieldType: "file",
      validation: Yup.object({
        files: Yup.array().min(1, "Application cannot be submitted unless a document has been uploaded"),
      }),
      label:
        "Please upload evidence from a medical practitioner that the child has complex medical needs which require a high level of supervision and individualised health care support during the kindergarten program",
      warn: (
        <span className="text-primary">
          This may be a letter, diagnostic report or similar from a medical practitioner that details the child's medical diagnosis.
          <p className="mt-1 m-0">
            For the purpose of the KIS application, a medical practitioner is a health professional registered under the Health Practitioner
            Regulation National Law to practice in the medical profession. These professionals include general practitioners, paediatricians,
            registered psychologists, medical specialists, and other medical professionals trained and qualified to diagnose and treat illnesses,
            injuries, and diseases. Drag and drop or choose file to attach documents{" "}
          </p>
        </span>
      ),
      hideExpression: (context) => {
        return !(
          (context.applicationBase?.applicationType?.applicationCategory === 0 ||
            context.applicationBase?.applicationType?.applicationCategory === 2) &&
          context.applicationBase?.screeningQuestions?.fundedKinder.consentYes.complexMedicalNeedsCriteria === 1
        );
      },
      expressionProps: {
        uuid: (context) => context.uuid,
        isManagerRole: (context) => context.isManagerRole,
        isPresub: (context) => context.isPresub,
        isCurrent: (context) => context.isCurrent,
        applicationBaseId: (context) => context.applicationBaseId,
        isAssessorRole: (context) => context.isAssessorRole,
        isKindergartenRole: (context) => context.isKindergartenRole,
      },
    },
    {
      type: "field",
      key: "riskMinimisationPlan",
      fieldType: "file",
      validation: Yup.object({
        files: Yup.array()
          .min(1, "Application cannot be submitted unless at least one document has been uploaded")
          .max(1, "Only one (1) file may be uploaded"),
      }),
      props: {
        maxNumberOfFiles: 1,
      },
      label: "Please upload a risk minimisation plan that addresses the complex medical needs of this child",
      warn: (
        <span className="text-primary">
          The risk minimisation plan should be completed in the format specified by the policies and procedures of your kindergarten service.
        </span>
      ),
      hideExpression: (context) => {
        return !(
          (context.applicationBase?.applicationType?.applicationCategory === 0 ||
            context.applicationBase?.applicationType?.applicationCategory === 2) &&
          context.applicationBase?.screeningQuestions?.fundedKinder.consentYes.complexMedicalNeedsCriteria === 1
        );
      },
      //dynamic set values
      expressionProps: {
        uuid: (context) => context.uuid,
        isManagerRole: (context) => context.isManagerRole,
        isAssessorRole: (context) => context.isAssessorRole,
        isPresub: (context) => context.isPresub,
        isCurrent: (context) => context.isCurrent,
        applicationBaseId: (context) => context.applicationBaseId,
        isKindergartenRole: (context) => context.isKindergartenRole,
      },
    },
    {
      type: "field",
      key: "currentMedicalManagementPlan",
      fieldType: "file",
      validation: Yup.object({
        files: Yup.array()
          .min(1, "Application cannot be submitted unless at least one document has been uploaded")
          .max(1, "Only one (1) file may be uploaded"),
      }),
      props: {
        maxNumberOfFiles: 1,
      },
      label: "Please upload a current medical management plan that addresses the complex medical needs of this child",
      warn: (
        <span className="text-primary">
          A medical management plan (MMP) should be prepared or endorsed by a medical practitioner, or medical professionals who are involved in the
          care/support (e.g. Diabetes Educator/Nurse) of the child in relation to their medical needs. This is to ensure that the support provided to
          the child and any training undertaken is in line with medical advice.
        </span>
      ),
      hideExpression: (context) => {
        return !(
          (context.applicationBase?.applicationType?.applicationCategory === 0 ||
            context.applicationBase?.applicationType?.applicationCategory === 2) &&
          context.applicationBase?.screeningQuestions?.fundedKinder.consentYes.complexMedicalNeedsCriteria === 1
        );
      },
      //dynamic set values
      expressionProps: {
        uuid: (context) => context.uuid,
        isManagerRole: (context) => context.isManagerRole,
        isAssessorRole: (context) => context.isAssessorRole,
        isPresub: (context) => context.isPresub,
        isCurrent: (context) => context.isCurrent,
        applicationBaseId: (context) => context.applicationBaseId,
        isKindergartenRole: (context) => context.isKindergartenRole,
      },
    },
    {
      type: "field",
      key: "disabilityDiagnosisEvidencePlan",
      fieldType: "file",
      validation: Yup.object({
        files: Yup.array().min(1, "Application cannot be submitted unless a document has been uploaded"),
      }),
      label:
        "Please upload evidence from a paediatrician, specialist medical practitioner or registered psychologist, such as a letter or a report that details the disability diagnosis",
      hideExpression: (context) => {
        return (
          context.applicationBase?.screeningQuestions?.fundedKinder.consentYes.typeOfDiagnosedDisabilityEvidence !== 1 ||
          context.applicationBase?.applicationType?.disabilityType != 1
        );
      },
      //dynamic set values
      expressionProps: {
        uuid: (context) => context.uuid,
        isManagerRole: (context) => context.isManagerRole,
        isAssessorRole: (context) => context.isAssessorRole,
        isPresub: (context) => context.isPresub,
        isCurrent: (context) => context.isCurrent,
        applicationBaseId: (context) => context.applicationBaseId,
        isKindergartenRole: (context) => context.isKindergartenRole,
      },
    },
    {
      type: "field",
      key: "currentNdisPlan",
      fieldType: "file",
      validation: Yup.object({
        files: Yup.array().min(1, "Application cannot be submitted unless a document has been uploaded"),
      }),
      label: "Please upload a current NDIS plan",
      warn: (
        <span className="text-primary">
          You only need to upload the page of the plan that shows the child’s details and plan review date, to show that the child has been accepted
          by NDIS and the end date of the plan. The whole plan is not required.
          <br />
          <br />
          The end date of the plan must be after the date this KIS application is submitted. If the end date of the plan is before the KIS application
          was submitted, a rolled over plan can be submitted if other evidence is uploaded to confirm that the plan has been extended, such as a
          screenshot from the NDIA portal that shows the new end date, the NDIS number and the child's name.
          <br />
          <br />
          Evidence showing that a NDIS application has been lodged is not accepted for the KIS application and should not be submitted.
        </span>
      ),
      hideExpression: (context) => {
        return (
          context.applicationBase?.screeningQuestions?.fundedKinder.consentYes.typeOfDiagnosedDisabilityEvidence !== 2 ||
          context.applicationBase?.applicationType.disabilityType !== 1
        );
      },
      //dynamic set values
      expressionProps: {
        uuid: (context) => context.uuid,
        isManagerRole: (context) => context.isManagerRole,
        isAssessorRole: (context) => context.isAssessorRole,
        isPresub: (context) => context.isPresub,
        isCurrent: (context) => context.isCurrent,
        applicationBaseId: (context) => context.applicationBaseId,
        isKindergartenRole: (context) => context.isKindergartenRole,
      },
    },
    {
      type: "field",
      key: "eligibleSupportEvidence",
      fieldType: "file",
      validation: Yup.object({
        files: Yup.array().min(1, "Application cannot be submitted unless a document has been uploaded"),
      }),
      label:
        "Please upload evidence the child is eligible for support through the Victorian government Early Childhood Intervention Services (ECIS) Continuity of Support (CoS) - note this is not support provided through the NDIA",
      warn: (
        <span className="text-primary">
          This program applies to children that would otherwise be eligible for NDIS support, but do not meet the residency requirements.
          <br />
          <br />A letter from an ECIS CoS provider can be used as evidence for this upload.
        </span>
      ),
      hideExpression: (context) => {
        return !(
          context.applicationBase?.applicationType?.disabilityType == 1 &&
          context.applicationBase?.screeningQuestions?.fundedKinder.consentYes.typeOfDiagnosedDisabilityEvidence == 3
        );
      },
      //dynamic set values
      expressionProps: {
        uuid: (context) => context.uuid,
        isManagerRole: (context) => context.isManagerRole,
        isAssessorRole: (context) => context.isAssessorRole,
        isPresub: (context) => context.isPresub,
        isCurrent: (context) => context.isCurrent,
        applicationBaseId: (context) => context.applicationBaseId,
        isKindergartenRole: (context) => context.isKindergartenRole,
      },
    },
    {
      type: "field",
      key: "appointmentLetterForAssessment",
      fieldType: "file",
      validation: Yup.object({
        files: Yup.array()
          .min(1, "Application cannot be submitted unless at least one document has been uploaded")
          .max(1, "Only one (1) file may be uploaded"),
      }),
      props: {
        maxNumberOfFiles: 1,
      },
      label:
        "Please upload an appointment letter for assessment of disability or developmental delay by a paediatrician, specialist medical practitioner or registered psychologist",
      hideExpression: (context) => {
        return !(
          context.applicationBase?.applicationType?.disabilityType == 2 &&
          context.applicationBase?.screeningQuestions?.fundedKinder.consentYes.evidenceUndergoingAssessment == 1
        );
      },
      //dynamic set values
      expressionProps: {
        uuid: (context) => context.uuid,
        isManagerRole: (context) => context.isManagerRole,
        isAssessorRole: (context) => context.isAssessorRole,
        isPresub: (context) => context.isPresub,
        isCurrent: (context) => context.isCurrent,
        applicationBaseId: (context) => context.applicationBaseId,
        isKindergartenRole: (context) => context.isKindergartenRole,
      },
    },
    {
      type: "field",
      key: "developmentAndDisabilityAssessmentDocumentation",
      fieldType: "file",
      validation: Yup.object({
        files: Yup.array()
          .min(1, "Application cannot be submitted unless at least one document has been uploaded")
          .max(1, "Only one (1) file may be uploaded"),
      }),
      props: {
        maxNumberOfFiles: 1,
      },
      label:
        "Please upload supporting documentation from a professional outlining the child’s development and confirming that the child has been referred for assessment of disability",
      hideExpression: (context) => {
        return !(
          context.applicationBase?.applicationType?.disabilityType === 2 &&
          context.applicationBase?.screeningQuestions?.fundedKinder.consentYes.evidenceUndergoingAssessment == 1
        );
      },
      //dynamic set values
      expressionProps: {
        uuid: (context) => context.uuid,
        isManagerRole: (context) => context.isManagerRole,
        isAssessorRole: (context) => context.isAssessorRole,
        isPresub: (context) => context.isPresub,
        isCurrent: (context) => context.isCurrent,
        applicationBaseId: (context) => context.applicationBaseId,
        isKindergartenRole: (context) => context.isKindergartenRole,
      },
    },
    {
      type: "field",
      key: "furtherContactDocumentation",
      fieldType: "file",
      validation: Yup.object({
        files: Yup.array()
          .min(1, "Application cannot be submitted unless at least one document has been uploaded")
          .max(1, "Only one (1) file may be uploaded"),
      }),
      props: {
        maxNumberOfFiles: 1,
      },
      label:
        "Please upload documentation from a paediatrician, specialist medical practitioner or registered psychologist showing that they will have further contact with the child for the purpose of ongoing assessment for a disability",
      hideExpression: (context) => {
        return (
          context.applicationBase?.applicationType.disabilityType !== 2 ||
          context.applicationBase?.screeningQuestions?.fundedKinder.consentYes.evidenceUndergoingAssessment !== 2
        );
      },
      //dynamic set values
      expressionProps: {
        uuid: (context) => context.uuid,
        isManagerRole: (context) => context.isManagerRole,
        isAssessorRole: (context) => context.isAssessorRole,
        isPresub: (context) => context.isPresub,
        isCurrent: (context) => context.isCurrent,
        applicationBaseId: (context) => context.applicationBaseId,
        isKindergartenRole: (context) => context.isKindergartenRole,
      },
    },
    {
      type: "field",
      key: "kisPlan",
      fieldType: "file",
      validation: Yup.object({
        files: Yup.array()
          .min(1, "Application cannot be submitted unless at least one document has been uploaded")
          .max(1, "Only one (1) file may be uploaded"),
      }),
      props: {
        maxNumberOfFiles: 1,
      },
      label: "Please upload a copy of the KIS plan for the child",
      warn: (
        <span className="text-primary">
          The KIS plan needs to be completed in the following{" "}
          <a
            className="underline "
            href="https://www.ahaconsulting.com.au/wp-content/uploads/2025/08/KIS-plan.pdf"
            target="_blank"
            title="Link opens in a new tab"
            rel="noreferrer">
            template.
            <br />
            <br />
          </a>{" "}
          For appeal applications, the KIS plan must have been completed or revised within the last 3 months.
        </span>
      ),
      hideExpression: (context) => {
        return !(
          context.applicationBase?.applicationType?.applicationCategory === 1 || context.applicationBase?.applicationType?.applicationCategory === 2
        );
      },
      //dynamic set values
      expressionProps: {
        uuid: (context) => context.uuid,
        isManagerRole: (context) => context.isManagerRole,
        isAssessorRole: (context) => context.isAssessorRole,
        isPresub: (context) => context.isPresub,
        isCurrent: (context) => context.isCurrent,
        applicationBaseId: (context) => context.applicationBaseId,
        isKindergartenRole: (context) => context.isKindergartenRole,
      },
    },
    {
      type: "field",
      key: "bushKinderRiskMinimisationPlan",
      fieldType: "file",
      validation: Yup.object({
        files: Yup.array()
          .min(1, "Application cannot be submitted unless at least one document has been uploaded")
          .max(1, "Only one (1) file may be uploaded"),
      }),
      props: {
        maxNumberOfFiles: 1,
      },
      label: "Please upload the Bush Kinder Risk Minimisation Plan",
      warn: (
        <span className="text-primary">
          A risk minimisation plan should be completed using your kindergarten service's template, and should be for the program/year you are applying
          for KIS support
        </span>
      ),
      hideExpression: (context) => {
        return context.applicationBase?.groupInformation?.doesBushKinderSessions !== 1;
      },
      //dynamic set values
      expressionProps: {
        uuid: (context) => context.uuid,
        isManagerRole: (context) => context.isManagerRole,
        isAssessorRole: (context) => context.isAssessorRole,
        isPresub: (context) => context.isPresub,
        isCurrent: (context) => context.isCurrent,
        applicationBaseId: (context) => context.applicationBaseId,
        isKindergartenRole: (context) => context.isKindergartenRole,
      },
    },
    {
      type: "field",
      key: "quotesForSpecialist",
      fieldType: "file",
      validation: Yup.object({
        files: Yup.array().min(1, "Application cannot be submitted unless a document has been uploaded").max(2, "Only two (2) files may be uploaded"),
      }),
      props: {
        maxNumberOfFiles: 2,
      },
      label: "Please upload two quotes for specialist training, specific to supporting the needs of the child",
      warn: (
        <span className="text-primary">
          Please note that that if you do not have one or more quotes to attach, you should not submit an application requesting specialist training.
          You can lodge an appeal once you receive an outcome so that minor building modifications can be considered.
          <br />
          <br />
          Please change your answers in the Support Requested question section so that this field does not display.
          <br />
          <br />
          If you have not uploaded one quote only and have not provided an explanation in question 3.4, this will delay the processing of your
          application.
        </span>
      ),
      hideExpression: (context) => {
        return context.applicationBase?.supportRequested === null || context.applicationBase?.supportRequested?.specialistTraining === false;
      },
      //dynamic set values
      expressionProps: {
        uuid: (context) => context.uuid,
        isManagerRole: (context) => context.isManagerRole,
        isAssessorRole: (context) => context.isAssessorRole,
        isPresub: (context) => context.isPresub,
        isCurrent: (context) => context.isCurrent,
        applicationBaseId: (context) => context.applicationBaseId,
        isKindergartenRole: (context) => context.isKindergartenRole,
      },
    },
    {
      type: "field",
      key: "quotesForMinorBuildingModifications",
      fieldType: "file",
      validation: Yup.object({
        files: Yup.array()
          .min(1, "Application cannot be submitted unless at least one document has been uploaded")
          .max(2, "Only two (2) files may be uploadedd"),
      }),
      props: {
        maxNumberOfFiles: 2,
      },
      label: "Please upload two quotes for minor building modifications, specific to supporting the needs of the child",
      warn: (
        <span className="text-primary">
          Please note that if you do not have one or more quotes to attach, you should not submit an application requesting minor building
          modifications. You can lodge an appeal once you receive an outcome so that minor building modifications can be considered.
          <br />
          <br />
          Please change your answers in the Support Requested section of the application form so that this field does not display.
        </span>
      ),
      hideExpression: (context) => {
        return context.applicationBase?.supportRequested === null || context.applicationBase?.supportRequested?.buildingModifications === false;
      },
      //dynamic set values
      expressionProps: {
        uuid: (context) => context.uuid,
        isManagerRole: (context) => context.isManagerRole,
        isAssessorRole: (context) => context.isAssessorRole,
        isPresub: (context) => context.isPresub,
        isCurrent: (context) => context.isCurrent,
        applicationBaseId: (context) => context.applicationBaseId,
        isKindergartenRole: (context) => context.isKindergartenRole,
      },
    },
    {
      type: "formArray",
      key: "otherDocuments",
      props: {
        removeLabel: "Remove this File",
        addLabel: "Upload another File",
      },
      config: FormArrayConfig.DeleteCustom,
      mutateValues(formContext, initialValues, childModel) {
        if (!formContext.disabled && initialValues[0].dateCreated) {
          initialValues.unshift(childModel);
        }
        return initialValues;
      },
      fields: [
        {
          type: "field",
          key: "document",
          fieldType: "file",
          props: {
            isRequired: false,
          },
          label:
            "If there are any other documents you wish to upload (such as PSFO reports, allied heath letters etc), please provide a description here and upload your files",
          //dynamic set values
          extendedlabel:
            "Any documentation should be limited to providing further evidence of specific conditions or supports that have already been described in this application.",
          expressionProps: {
            uuid: (context) => context.uuid,
            isManagerRole: (context) => context.isManagerRole,
            isAssessorRole: (context) => context.isAssessorRole,
            isPresub: (context) => context.isPresub,
            isCurrent: (context) => context.isCurrent,
            applicationBaseId: (context) => context.applicationBaseId,
            isKindergartenRole: (context) => context.isKindergartenRole,
          },
        },
        {
          type: "field",
          fieldType: "input",
          key: "description",
          label: "Description",
          validation: Yup.string()
            .trim()
            .when(["document"], ([document], schema) => {
              if (!document || document?.files?.length < 1) {
                return schema.notRequired();
              }
              return schema.required("A response is required");
            }),
        },
      ],
    },
  ],
};
